import React, { useContext, useEffect, useState, useRef, useCallback, memo } from "react";
import { AuthContext } from "../App";
import axios from "axios";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { 
  MessageCircle, 
  Send, 
  Search, 
  Megaphone, 
  Check, 
  CheckCheck,
  Circle,
  Users,
  ArrowLeft,
  Trash2,
  AlertCircle
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

// Memoized Message Item
const MessageItem = memo(({ msg, isOwn, formatMessageTime, onDelete }) => {
  const isPending = msg.pending;
  const [showDelete, setShowDelete] = useState(false);
  
  return (
    <div 
      className={`flex ${isOwn ? 'justify-end' : 'justify-start'} group`}
      onMouseEnter={() => isOwn && setShowDelete(true)}
      onMouseLeave={() => setShowDelete(false)}
    >
      {/* Delete button for own messages */}
      {isOwn && showDelete && !isPending && (
        <button
          onClick={() => onDelete(msg.message_id)}
          className="self-center mr-2 p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
          title="Mesajı sil"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}
      <div
        className={`max-w-[70%] p-3 rounded-2xl ${
          isOwn
            ? isPending 
              ? 'bg-[#1A4D2E]/70 text-white rounded-br-md'
              : 'bg-[#1A4D2E] text-white rounded-br-md'
            : 'bg-white text-gray-900 rounded-bl-md shadow-sm'
        }`}
      >
        <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
        <div className={`flex items-center justify-end gap-1 mt-1 ${isOwn ? 'text-white/70' : 'text-gray-400'}`}>
          <span className="text-xs">{formatMessageTime(msg.created_at)}</span>
          {isOwn && (
            isPending ? (
              <Circle className="w-3 h-3" />
            ) : msg.is_read ? (
              <CheckCheck className="w-4 h-4 text-blue-300" />
            ) : (
              <Check className="w-4 h-4" />
            )
          )}
        </div>
      </div>
    </div>
  );
});

// Memoized Chat Input - KEY FIX: Separate component to prevent re-renders
const ChatInput = memo(({ onSend }) => {
  const [text, setText] = useState("");
  const inputRef = useRef(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    onSend(text.trim());
    setText("");
    // Keep focus on input
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  return (
    <form onSubmit={handleSubmit} className="p-4 bg-white border-t border-gray-200">
      <div className="flex gap-2">
        <Input
          ref={inputRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Mesaj yazın..."
          className="flex-1"
          autoComplete="off"
        />
        <Button type="submit" className="bg-[#1A4D2E] hover:bg-[#1A4D2E]/90" disabled={!text.trim()}>
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </form>
  );
});

const MessagesPage = () => {
  const { user } = useContext(AuthContext);
  const [conversations, setConversations] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [users, setUsers] = useState([]);
  const [showUserList, setShowUserList] = useState(false);
  const [showAnnouncements, setShowAnnouncements] = useState(false);
  const [loading, setLoading] = useState(true);
  const [typing, setTyping] = useState({});
  const [onlineUsers, setOnlineUsers] = useState({});
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  const [showChat, setShowChat] = useState(false);
  const [totalUnread, setTotalUnread] = useState(0);
  
  // Announcement dialog
  const [announcementDialog, setAnnouncementDialog] = useState(false);
  const [announcementTitle, setAnnouncementTitle] = useState("");
  const [announcementContent, setAnnouncementContent] = useState("");
  
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const selectedConvRef = useRef(null);

  // Keep ref in sync
  useEffect(() => {
    selectedConvRef.current = selectedConversation;
  }, [selectedConversation]);

  // Responsive handler
  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch initial data
  useEffect(() => {
    fetchConversations();
    fetchAnnouncements();
    fetchUsers();
  }, []);

  // WebSocket connection
  useEffect(() => {
    if (!user) return;

    const connectWebSocket = () => {
      wsRef.current = new WebSocket(`${WS_URL}/api/ws/messages/${user.user_id}`);
      
      wsRef.current.onopen = () => {
        console.log("Messages WebSocket connected");
      };
      
      wsRef.current.onmessage = (event) => {
        const data = JSON.parse(event.data);
        handleWebSocketMessage(data);
      };
      
      wsRef.current.onclose = () => {
        console.log("Messages WebSocket disconnected, reconnecting...");
        setTimeout(connectWebSocket, 3000);
      };
    };
    
    connectWebSocket();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [user]);

  const handleWebSocketMessage = useCallback((data) => {
    switch (data.type) {
      case "new_message":
        // Update messages if in same conversation (use ref to get current value)
        if (selectedConvRef.current?.conversation_id === data.conversation_id) {
          setMessages(prev => [...prev, data.message]);
          markMessagesRead(data.conversation_id);
        }
        // Update conversation list
        fetchConversations();
        break;
        
      case "message_read":
      case "messages_read":
        setMessages(prev => prev.map(msg => ({
          ...msg,
          is_read: msg.conversation_id === data.conversation_id ? true : msg.is_read
        })));
        break;
        
      case "typing":
        setTyping(prev => ({ ...prev, [data.user_id]: true }));
        setTimeout(() => {
          setTyping(prev => ({ ...prev, [data.user_id]: false }));
        }, 3000);
        break;
        
      case "user_online":
        setOnlineUsers(prev => ({ ...prev, [data.user_id]: true }));
        break;
        
      case "user_offline":
        setOnlineUsers(prev => ({ ...prev, [data.user_id]: false }));
        break;
      
      case "message_deleted":
        // Remove deleted message from UI
        setMessages(prev => prev.filter(msg => msg.message_id !== data.message_id));
        // Update conversation list
        setTimeout(() => fetchConversations(), 100);
        break;
        
      case "new_announcement":
        setAnnouncements(prev => [data.announcement, ...prev]);
        toast.info(`📢 ${data.announcement.title}`);
        break;
      
      case "announcement_deleted":
        setAnnouncements(prev => prev.filter(a => a.announcement_id !== data.announcement_id));
        break;
      
      case "conversations_cleared":
        // All conversations deleted by super admin
        setConversations([]);
        setSelectedConversation(null);
        setMessages([]);
        toast.info("Tüm konuşmalar silindi");
        break;
      
      case "conversation_deleted":
        // Single conversation deleted
        setConversations(prev => prev.filter(c => c.conversation_id !== data.conversation_id));
        if (selectedConvRef.current?.conversation_id === data.conversation_id) {
          setSelectedConversation(null);
          setMessages([]);
        }
        break;
        
      default:
        break;
    }
  }, []);

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/messages/conversations`, { withCredentials: true });
      setConversations(response.data);
      
      // Calculate total unread
      let unread = 0;
      const online = {};
      response.data.forEach(conv => {
        unread += conv.unread_count?.[user?.user_id] || 0;
        Object.entries(conv.online_status || {}).forEach(([uid, status]) => {
          online[uid] = status;
        });
      });
      setTotalUnread(unread);
      setOnlineUsers(prev => ({ ...prev, ...online }));
    } catch (error) {
      console.error("Failed to fetch conversations:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnnouncements = async () => {
    try {
      const response = await axios.get(`${API}/announcements`, { withCredentials: true });
      setAnnouncements(response.data);
    } catch (error) {
      console.error("Failed to fetch announcements:", error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/messages/users`, { withCredentials: true });
      setUsers(response.data);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const response = await axios.get(`${API}/messages/${conversationId}`, { withCredentials: true });
      setMessages(response.data);
      scrollToBottom();
    } catch (error) {
      console.error("Failed to fetch messages:", error);
    }
  };

  const markMessagesRead = async (conversationId) => {
    try {
      await axios.post(`${API}/messages/${conversationId}/read`, {}, { withCredentials: true });
      fetchConversations();
    } catch (error) {
      console.error("Failed to mark messages as read:", error);
    }
  };

  const selectConversation = async (conversation) => {
    setSelectedConversation(conversation);
    setShowAnnouncements(false);
    await fetchMessages(conversation.conversation_id);
    if (isMobileView) setShowChat(true);
  };

  const startNewConversation = async (otherUser) => {
    try {
      const response = await axios.get(`${API}/messages/conversation/${otherUser.user_id}`, { withCredentials: true });
      setSelectedConversation(response.data);
      setShowAnnouncements(false);
      setShowUserList(false);
      await fetchMessages(response.data.conversation_id);
      fetchConversations();
      if (isMobileView) setShowChat(true);
    } catch (error) {
      toast.error("Sohbet başlatılamadı");
    }
  };

  // Send message handler - passed to ChatInput
  const handleSendMessage = useCallback(async (content) => {
    if (!selectedConversation) return;

    const tempMessageId = `temp_${Date.now()}`;
    
    // Create temporary message
    const tempMessage = {
      message_id: tempMessageId,
      conversation_id: selectedConversation.conversation_id,
      sender_id: user.user_id,
      sender_name: user.name,
      sender_picture: user.picture,
      content: content,
      is_read: false,
      read_at: null,
      created_at: new Date().toISOString(),
      pending: true
    };
    
    // Add to UI immediately
    setMessages(prev => [...prev, tempMessage]);
    scrollToBottom();

    try {
      const response = await axios.post(
        `${API}/messages/${selectedConversation.conversation_id}?content=${encodeURIComponent(content)}`,
        {},
        { withCredentials: true }
      );
      
      // Replace temp message with real one
      setMessages(prev => prev.map(msg => 
        msg.message_id === tempMessageId ? { ...response.data, pending: false } : msg
      ));
      
      fetchConversations();
    } catch (error) {
      console.error("Send message error:", error);
      setMessages(prev => prev.filter(msg => msg.message_id !== tempMessageId));
      toast.error("Mesaj gönderilemedi");
    }
  }, [selectedConversation, user]);

  const deleteMessage = async (messageId) => {
    if (!selectedConversation) return;
    
    try {
      await axios.delete(
        `${API}/messages/${selectedConversation.conversation_id}/${messageId}`,
        { withCredentials: true }
      );
      setMessages(prev => prev.filter(msg => msg.message_id !== messageId));
      toast.success("Mesaj silindi");
      setTimeout(() => fetchConversations(), 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Mesaj silinemedi");
    }
  };

  const createAnnouncement = async () => {
    if (!announcementTitle.trim() || !announcementContent.trim()) {
      toast.error("Başlık ve içerik gerekli");
      return;
    }

    try {
      await axios.post(
        `${API}/announcements?title=${encodeURIComponent(announcementTitle)}&content=${encodeURIComponent(announcementContent)}`,
        {},
        { withCredentials: true }
      );
      toast.success("Duyuru gönderildi");
      setAnnouncementDialog(false);
      setAnnouncementTitle("");
      setAnnouncementContent("");
      fetchAnnouncements();
    } catch (error) {
      toast.error("Duyuru gönderilemedi");
    }
  };

  const deleteAnnouncement = async (announcementId) => {
    if (!window.confirm("Bu duyuruyu silmek istediğinize emin misiniz?")) return;
    
    try {
      await axios.delete(`${API}/announcements/${announcementId}`, { withCredentials: true });
      toast.success("Duyuru silindi");
      setAnnouncements(prev => prev.filter(a => a.announcement_id !== announcementId));
    } catch (error) {
      toast.error("Duyuru silinemedi");
    }
  };

  const deleteAllConversations = async () => {
    if (!window.confirm("⚠️ DİKKAT: Tüm konuşmalar ve mesajlar kalıcı olarak silinecek!\n\nBu işlem geri alınamaz. Devam etmek istediğinize emin misiniz?")) return;
    
    try {
      const response = await axios.delete(`${API}/messages/all`, { withCredentials: true });
      toast.success(`${response.data.deleted_conversations} konuşma ve ${response.data.deleted_messages} mesaj silindi`);
      setConversations([]);
      setSelectedConversation(null);
      setMessages([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Konuşmalar silinemedi");
    }
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const getOtherParticipant = (conversation) => {
    const otherId = conversation.participants.find(p => p !== user?.user_id);
    return {
      id: otherId,
      name: conversation.participant_names?.[otherId] || "Bilinmeyen",
      picture: conversation.participant_pictures?.[otherId]
    };
  };

  const getInitials = (name) => {
    return name?.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) || "?";
  };

  const formatMessageTime = useCallback((dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return format(date, "HH:mm", { locale: tr });
    } else if (diffDays === 1) {
      return "Dün " + format(date, "HH:mm", { locale: tr });
    } else if (diffDays < 7) {
      return format(date, "EEEE HH:mm", { locale: tr });
    } else {
      return format(date, "dd MMM HH:mm", { locale: tr });
    }
  }, []);

  const filteredConversations = conversations.filter(conv => {
    const other = getOtherParticipant(conv);
    return other.name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const filteredUsers = users.filter(u => 
    u.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-200px)]">
        <div className="w-8 h-8 border-4 border-[#1A4D2E] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Sidebar content
  const renderSidebar = () => (
    <div className="flex flex-col h-full bg-white border-r border-gray-200">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-bold text-[#1A4D2E]">Mesajlar</h2>
            {totalUnread > 0 && (
              <span className="px-2 py-0.5 bg-[#1A4D2E] text-white text-xs rounded-full">
                {totalUnread}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            {(user?.role === "admin" || user?.role === "super_admin") && (
              <Dialog open={announcementDialog} onOpenChange={setAnnouncementDialog}>
                <DialogTrigger asChild>
                  <Button size="icon" variant="outline" title="Duyuru Yap">
                    <Megaphone className="w-4 h-4" />
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Yeni Duyuru</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Input
                        placeholder="Duyuru başlığı"
                        value={announcementTitle}
                        onChange={(e) => setAnnouncementTitle(e.target.value)}
                      />
                    </div>
                    <div>
                      <Textarea
                        placeholder="Duyuru içeriği..."
                        value={announcementContent}
                        onChange={(e) => setAnnouncementContent(e.target.value)}
                        rows={4}
                      />
                    </div>
                    <Button onClick={createAnnouncement} className="w-full bg-[#1A4D2E]">
                      <Megaphone className="w-4 h-4 mr-2" />
                      Duyuru Gönder
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
            {user?.role === "super_admin" && (
              <Button 
                size="icon" 
                variant="outline" 
                onClick={deleteAllConversations} 
                title="Tüm Konuşmaları Sil"
                className="text-red-500 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
            <Button size="icon" variant="outline" onClick={() => setShowUserList(!showUserList)} title="Yeni Sohbet">
              <Users className="w-4 h-4" />
            </Button>
          </div>
        </div>
        
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => { setShowAnnouncements(false); setShowUserList(false); }}
          className={`flex-1 py-3 text-sm font-medium ${!showAnnouncements && !showUserList ? 'text-[#1A4D2E] border-b-2 border-[#1A4D2E]' : 'text-gray-500'}`}
        >
          Sohbetler
        </button>
        <button
          onClick={() => { setShowAnnouncements(true); setShowUserList(false); setSelectedConversation(null); }}
          className={`flex-1 py-3 text-sm font-medium ${showAnnouncements ? 'text-[#1A4D2E] border-b-2 border-[#1A4D2E]' : 'text-gray-500'}`}
        >
          Duyurular
        </button>
      </div>

      {/* Content */}
      <ScrollArea className="flex-1">
        {showUserList ? (
          <div className="p-2">
            <p className="px-3 py-2 text-sm text-gray-500">Yeni sohbet başlat</p>
            {filteredUsers.map((u) => (
              <button
                key={u.user_id}
                onClick={() => startNewConversation(u)}
                className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="relative">
                  <Avatar>
                    <AvatarImage src={u.picture} />
                    <AvatarFallback className="bg-[#1A4D2E] text-white">
                      {getInitials(u.name)}
                    </AvatarFallback>
                  </Avatar>
                  {u.is_online && (
                    <Circle className="absolute bottom-0 right-0 w-3 h-3 fill-green-500 text-green-500" />
                  )}
                </div>
                <div className="text-left">
                  <p className="font-medium text-gray-900">{u.name}</p>
                  <p className="text-xs text-gray-500 capitalize">{u.role?.replace('_', ' ')}</p>
                </div>
              </button>
            ))}
          </div>
        ) : showAnnouncements ? (
          <div className="p-2">
            <div className="mb-3 p-2 bg-blue-50 rounded-lg border border-blue-200 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-blue-700">Duyurular kalıcıdır ve admin/süper admin tarafından silinebilir.</p>
            </div>
            {announcements.length === 0 ? (
              <p className="text-center text-gray-500 py-8">Henüz duyuru yok</p>
            ) : (
              announcements.map((ann) => (
                <div key={ann.announcement_id} className="p-4 mb-2 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Megaphone className="w-4 h-4 text-amber-600" />
                      <span className="font-semibold text-amber-800">{ann.title}</span>
                    </div>
                    {(user?.role === "admin" || user?.role === "super_admin") && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-gray-400 hover:text-red-500"
                        onClick={() => deleteAnnouncement(ann.announcement_id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <p className="text-sm text-gray-700">{ann.content}</p>
                  <p className="text-xs text-gray-500 mt-2">
                    {ann.sender_name} • {formatMessageTime(ann.created_at)}
                  </p>
                </div>
              ))
            )}
          </div>
        ) : (
          <div className="p-2">
            <div className="mb-3 p-2 bg-amber-50 rounded-lg border border-amber-200 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-700">Mesajlar 7 gün sonra otomatik silinir.</p>
            </div>
            {filteredConversations.length === 0 ? (
              <div className="text-center py-8">
                <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500">Henüz sohbet yok</p>
                <Button 
                  variant="link" 
                  onClick={() => setShowUserList(true)}
                  className="text-[#1A4D2E]"
                >
                  Yeni sohbet başlat
                </Button>
              </div>
            ) : (
              filteredConversations.map((conv) => {
                const other = getOtherParticipant(conv);
                const unread = conv.unread_count?.[user?.user_id] || 0;
                const isOnline = onlineUsers[other.id];
                const isSelected = selectedConversation?.conversation_id === conv.conversation_id;
                
                return (
                  <button
                    key={conv.conversation_id}
                    onClick={() => selectConversation(conv)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${
                      isSelected ? 'bg-[#E8F3EE]' : 'hover:bg-gray-100'
                    }`}
                  >
                    <div className="relative">
                      <Avatar>
                        <AvatarImage src={other.picture} />
                        <AvatarFallback className="bg-[#1A4D2E] text-white">
                          {getInitials(other.name)}
                        </AvatarFallback>
                      </Avatar>
                      {isOnline && (
                        <Circle className="absolute bottom-0 right-0 w-3 h-3 fill-green-500 text-green-500" />
                      )}
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-gray-900 truncate">{other.name}</p>
                        {conv.last_message_time && (
                          <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                            {formatMessageTime(conv.last_message_time)}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-gray-500 truncate">
                          {typing[other.id] ? (
                            <span className="text-[#1A4D2E] italic">yazıyor...</span>
                          ) : (
                            conv.last_message || "Henüz mesaj yok"
                          )}
                        </p>
                        {unread > 0 && (
                          <span className="ml-2 w-5 h-5 bg-[#1A4D2E] text-white text-xs rounded-full flex items-center justify-center flex-shrink-0">
                            {unread}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        )}
      </ScrollArea>
    </div>
  );

  // Chat area content
  const renderChatArea = () => {
    if (showAnnouncements) {
      return (
        <div className="flex-1 flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <Megaphone className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Duyuruları sol panelden görüntüleyin</p>
          </div>
        </div>
      );
    }

    if (!selectedConversation) {
      return (
        <div className="flex-1 flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <MessageCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Bir sohbet seçin veya yeni sohbet başlatın</p>
          </div>
        </div>
      );
    }

    const other = getOtherParticipant(selectedConversation);
    const isOnline = onlineUsers[other.id];
    const isTyping = typing[other.id];

    return (
      <div className="flex-1 flex flex-col bg-gray-50">
        {/* Chat header */}
        <div className="p-4 bg-white border-b border-gray-200 flex items-center gap-3">
          {isMobileView && (
            <Button variant="ghost" size="icon" onClick={() => setShowChat(false)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
          )}
          <div className="relative">
            <Avatar>
              <AvatarImage src={other.picture} />
              <AvatarFallback className="bg-[#1A4D2E] text-white">
                {getInitials(other.name)}
              </AvatarFallback>
            </Avatar>
            {isOnline && (
              <Circle className="absolute bottom-0 right-0 w-3 h-3 fill-green-500 text-green-500" />
            )}
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{other.name}</h3>
            <p className="text-xs text-gray-500">
              {isTyping ? (
                <span className="text-[#1A4D2E]">yazıyor...</span>
              ) : isOnline ? (
                <span className="text-green-600">Çevrimiçi</span>
              ) : (
                "Çevrimdışı"
              )}
            </p>
          </div>
        </div>

        {/* Messages */}
        <ScrollArea className="flex-1 p-4">
          <div className="space-y-4">
            {messages.map((msg) => (
              <MessageItem 
                key={msg.message_id} 
                msg={msg} 
                isOwn={msg.sender_id === user?.user_id}
                formatMessageTime={formatMessageTime}
                onDelete={deleteMessage}
              />
            ))}
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        {/* Message input - Memoized component */}
        <ChatInput onSend={handleSendMessage} />
      </div>
    );
  };

  return (
    <div className="h-[calc(100vh-120px)]" data-testid="messages-page">
      <Card className="h-full overflow-hidden border-[#E8F3EE]">
        <div className="flex h-full">
          {isMobileView ? (
            showChat ? renderChatArea() : renderSidebar()
          ) : (
            <>
              <div className="w-80 flex-shrink-0 h-full">
                {renderSidebar()}
              </div>
              {renderChatArea()}
            </>
          )}
        </div>
      </Card>
    </div>
  );
};

export default MessagesPage;
